import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '../../../../config/theme';
import { useStax } from '../../providers/StaxProvider';
import { staxConfig } from '../../../../config/stax';

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
  width: 100%;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const Label = styled.label`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: 0.875rem;
  font-weight: 500;
  color: ${theme.colors.primary.text};
  letter-spacing: 0.02em;
`;

const Input = styled.input`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: ${theme.typography.secondary.body.fontSize};
  color: ${theme.colors.primary.text};
  background: ${theme.colors.primary.background};
  border: 1px solid ${theme.colors.accent.main};
  border-radius: 4px;
  padding: ${theme.spacing.md};
  min-height: 44px;
  transition: ${theme.transitions.default};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.buttons};
    box-shadow: 0 0 0 2px rgba(191, 161, 155, 0.2);
    background: rgba(255, 255, 255, 0.98);
  }
  
  &::placeholder {
    color: ${theme.colors.neutral.medium};
    opacity: 0.8;
  }
  
  &.error {
    border-color: #f44336;
  }
`;

const RowContainer = styled.div`
  display: grid;
  grid-template-columns: 100px 120px;
  gap: ${theme.spacing.md};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const ErrorMessage = styled.span`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: 0.875rem;
  color: #f44336;
`;

const Button = styled.button`
  font-family: ${theme.typography.button.fontFamily};
  font-size: ${theme.typography.button.fontSize};
  font-weight: ${theme.typography.button.fontWeight};
  letter-spacing: ${theme.typography.button.letterSpacing};
  text-transform: ${theme.typography.button.textTransform};
  
  background: transparent;
  color: ${theme.colors.primary.buttons};
  border: 1px solid ${theme.colors.primary.buttons};
  border-radius: 50px;
  
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  font-size: 0.875rem;
  min-height: 38px;
  height: 38px;
  cursor: pointer;
  transition: ${theme.transitions.default};
  text-decoration: none;
  
  &:hover:not(:disabled) {
    background: ${theme.colors.primary.buttons};
    color: ${theme.colors.primary.background};
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background: transparent;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid ${theme.colors.primary.background};
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 0.8s linear infinite;
  margin-right: ${theme.spacing.xs};
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const CardBrandIcon = styled.img`
  position: absolute;
  right: ${theme.spacing.md};
  top: 50%;
  transform: translateY(-50%);
  height: 24px;
  width: auto;
`;

const CardNumberWrapper = styled.div`
  position: relative;
`;

const StaxCardForm = ({ onSuccess, onCancel, setAsDefault = true }) => {
  const { tokenizeCard, validateCard, isLoading: staxLoading } = useStax();
  
  const [formData, setFormData] = useState({
    cardNumber: '',
    expMonth: '',
    expYear: '',
    cvc: '',
    cardholderName: '',
    zip: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cardBrand, setCardBrand] = useState(null);

  // Format card number with spaces
  const formatCardNumber = (value) => {
    const cleaned = value.replace(/\s/g, '');
    const chunks = cleaned.match(/.{1,4}/g) || [];
    return chunks.join(' ');
  };

  // Detect card brand from number
  const detectCardBrand = (number) => {
    const cleaned = number.replace(/\s/g, '');
    
    if (/^4/.test(cleaned)) return 'visa';
    if (/^5[1-5]/.test(cleaned)) return 'mastercard';
    if (/^3[47]/.test(cleaned)) return 'amex';
    if (/^6(?:011|5)/.test(cleaned)) return 'discover';
    
    return null;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    // Format based on field
    if (name === 'cardNumber') {
      formattedValue = formatCardNumber(value.replace(/\D/g, ''));
      setCardBrand(detectCardBrand(formattedValue));
    } else if (name === 'expMonth' || name === 'expYear') {
      formattedValue = value.replace(/\D/g, '');
    } else if (name === 'cvc') {
      formattedValue = value.replace(/\D/g, '').slice(0, 4);
    }

    setFormData(prev => ({
      ...prev,
      [name]: formattedValue
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (staxLoading) return;

    // Validate form
    const validationErrors = validateCard({
      ...formData,
      cardNumber: formData.cardNumber.replace(/\s/g, ''),
      expMonth: parseInt(formData.expMonth, 10),
      expYear: parseInt(formData.expYear, 10)
    });

    if (validationErrors) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      // Tokenize card with Stax
      const token = await tokenizeCard({
        cardNumber: formData.cardNumber.replace(/\s/g, ''),
        expMonth: parseInt(formData.expMonth, 10),
        expYear: parseInt(formData.expYear, 10),
        cvc: formData.cvc,
        cardholderName: formData.cardholderName,
        zip: formData.zip
      });

      // Call parent success handler with token and card details
      await onSuccess({
        token,
        cardDetails: {
          brand: cardBrand || 'generic',
          last4: formData.cardNumber.replace(/\s/g, '').slice(-4),
          expMonth: parseInt(formData.expMonth, 10),
          expYear: parseInt(formData.expYear, 10),
          cardholderName: formData.cardholderName
        },
        setAsDefault
      });
    } catch (error) {
      console.error('Card tokenization error:', error);
      setErrors({
        general: error.message || staxConfig.errorMessages.tokenization_failed
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormContainer onSubmit={handleSubmit}>
      {errors.general && (
        <ErrorMessage>{errors.general}</ErrorMessage>
      )}
      
      <FormGroup>
        <Label htmlFor="cardholderName">Cardholder Name</Label>
        <Input
          id="cardholderName"
          name="cardholderName"
          type="text"
          placeholder="John Doe"
          value={formData.cardholderName}
          onChange={handleInputChange}
          className={errors.cardholderName ? 'error' : ''}
          disabled={isSubmitting}
          required
        />
        {errors.cardholderName && (
          <ErrorMessage>{errors.cardholderName}</ErrorMessage>
        )}
      </FormGroup>

      <FormGroup>
        <Label htmlFor="cardNumber">Card Number</Label>
        <CardNumberWrapper>
          <Input
            id="cardNumber"
            name="cardNumber"
            type="text"
            placeholder="1234 5678 9012 3456"
            value={formData.cardNumber}
            onChange={handleInputChange}
            className={errors.cardNumber ? 'error' : ''}
            disabled={isSubmitting}
            maxLength="19"
            required
            style={{ paddingRight: cardBrand ? '50px' : undefined }}
          />
          {cardBrand && staxConfig.cardBrandLogos[cardBrand] && (
            <CardBrandIcon
              src={staxConfig.cardBrandLogos[cardBrand]}
              alt={cardBrand}
            />
          )}
        </CardNumberWrapper>
        {errors.cardNumber && (
          <ErrorMessage>{errors.cardNumber}</ErrorMessage>
        )}
      </FormGroup>

      <RowContainer>
        <FormGroup>
          <Label htmlFor="expMonth">Exp. Month</Label>
          <Input
            id="expMonth"
            name="expMonth"
            type="text"
            placeholder="MM"
            value={formData.expMonth}
            onChange={handleInputChange}
            className={errors.expMonth ? 'error' : ''}
            disabled={isSubmitting}
            maxLength="2"
            required
          />
          {errors.expMonth && (
            <ErrorMessage>{errors.expMonth}</ErrorMessage>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="expYear">Exp. Year</Label>
          <Input
            id="expYear"
            name="expYear"
            type="text"
            placeholder="YYYY"
            value={formData.expYear}
            onChange={handleInputChange}
            className={errors.expYear ? 'error' : ''}
            disabled={isSubmitting}
            maxLength="4"
            required
          />
          {errors.expYear && (
            <ErrorMessage>{errors.expYear}</ErrorMessage>
          )}
        </FormGroup>
      </RowContainer>

      <FormGroup>
        <Label htmlFor="cvc">Security Code (CVC)</Label>
        <Input
          id="cvc"
          name="cvc"
          type="text"
          placeholder="123"
          value={formData.cvc}
          onChange={handleInputChange}
          className={errors.cvc ? 'error' : ''}
          disabled={isSubmitting}
          maxLength="4"
          required
        />
        {errors.cvc && (
          <ErrorMessage>{errors.cvc}</ErrorMessage>
        )}
      </FormGroup>

      <FormGroup>
        <Label htmlFor="zip">ZIP Code</Label>
        <Input
          id="zip"
          name="zip"
          type="text"
          placeholder="12345"
          value={formData.zip}
          onChange={handleInputChange}
          className={errors.zip ? 'error' : ''}
          disabled={isSubmitting}
          maxLength="10"
          required
        />
        {errors.zip && (
          <ErrorMessage>{errors.zip}</ErrorMessage>
        )}
      </FormGroup>

      <FormGroup style={{ marginTop: theme.spacing.lg }}>
        <div style={{ display: 'flex', gap: theme.spacing.md, justifyContent: 'center' }}>
          <CancelButton
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </CancelButton>
          <Button
            type="submit"
            disabled={isSubmitting || staxLoading}
            style={{ 
              background: theme.colors.primary.buttons,
              color: theme.colors.primary.background,
              '&:hover:not(:disabled)': {
                background: theme.colors.primary.buttons,
                opacity: 0.9
              }
            }}
          >
            {isSubmitting && <LoadingSpinner />}
            {isSubmitting ? 'Processing...' : 'Add Card'}
          </Button>
        </div>
      </FormGroup>
    </FormContainer>
  );
};

export default StaxCardForm;